import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import QuratorDashboard from './pages/QuratorDashboard';
import Library from './pages/Library';
import TrainerSimulations from './pages/TrainerSimulations';
import PublishedCopilots from './pages/PublishedCopilots';
import Drafts from './pages/Drafts';
import AIAssistant from './pages/AIAssistant';
import HelpDesk from './pages/HelpDesk';
import EditDraft from './pages/EditDraft';
import DraftRead from './pages/DraftRead';
import PlaySimulation from './pages/PlaySimulation';
import Projects from './pages/Projects';
import TeamManagement from './pages/TeamManagement';
import GlobalContext from './pages/GlobalContext';
import Calendar from './pages/Calendar';
import Analytics from './pages/Analytics';
import SupportTicket from './pages/SupportTicket';
import QureDashboard from './pages/QureDashboard';
import MyTeams from './pages/MyTeams';
import QureCalendar from './pages/QureCalendar';
import QureAnalytics from './pages/QureAnalytics';
import QureLibrary from './pages/QureLibrary';
import Profile from './pages/Profile';
import HumanValidation from './pages/HumanValidation';
import Telepresence from './pages/Telepresence';
import RecordClip from './pages/RecordClip';
import RecordControl from './pages/RecordControl';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/AdminDashboard';

/** Root `/`: send logged-in users to their role dashboard (login handled by ProtectedRoute). */
function HomeRedirect() {
  const { user, getRedirectPath } = useAuth();
  const path = getRedirectPath(user?.user_type);
  return <Navigate to={path} replace />;
}

/** Preserve query string when redirecting legacy Qure calendar URLs. */
function LegacyQureCalendarRedirect() {
  const location = useLocation();
  return <Navigate to={`/qure-calendar${location.search || ''}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/telepresence" element={<Telepresence />} />
          <Route path="/record" element={<RecordClip />} />
          <Route path="/control" element={<RecordControl />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomeRedirect />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/qurator-dashboard" 
            element={
              <ProtectedRoute>
                <QuratorDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/library" 
            element={
              <ProtectedRoute>
                <Library />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/trainer-simulations" 
            element={
              <ProtectedRoute>
                <TrainerSimulations />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/published-copilots" 
            element={
              <ProtectedRoute>
                <PublishedCopilots />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/drafts" 
            element={
              <ProtectedRoute>
                <Drafts />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-assistant" 
            element={
              <ProtectedRoute>
                <AIAssistant />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help-desk" 
            element={
              <ProtectedRoute>
                <HelpDesk />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/draft-edit" 
            element={
              <ProtectedRoute>
                <EditDraft />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/draft-read" 
            element={
              <ProtectedRoute>
                <DraftRead />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/play-simulation" 
            element={
              <ProtectedRoute>
                <PlaySimulation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/projects" 
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/team-management" 
            element={
              <ProtectedRoute>
                <TeamManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/global-context" 
            element={
              <ProtectedRoute>
                <GlobalContext />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/calendar" 
            element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/support" 
            element={
              <ProtectedRoute>
                <SupportTicket />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/qure-dashboard" 
            element={
              <ProtectedRoute>
                <QureDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-teams" 
            element={
              <ProtectedRoute>
                <MyTeams />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/qure-calendar" 
            element={
              <ProtectedRoute>
                <QureCalendar />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/qure-analytics" 
            element={
              <ProtectedRoute>
                <QureAnalytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/qure-library" 
            element={
              <ProtectedRoute>
                <QureLibrary />
              </ProtectedRoute>
            } 
          />
          {/* Legacy path aliases (old bookmarks / Profile links) */}
          <Route path="/qure/dashboard" element={<Navigate to="/qure-dashboard" replace />} />
          <Route path="/qurator/dashboard" element={<Navigate to="/qurator-dashboard" replace />} />
          <Route path="/qure/calendar" element={<LegacyQureCalendarRedirect />} />
          <Route path="/qure/experiments" element={<Navigate to="/qure-library" replace />} />
          <Route path="/scheduled-experiments" element={<LegacyQureCalendarRedirect />} />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/human-validation" 
            element={
              <ProtectedRoute>
                <HumanValidation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin-dashboard" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;
